import _ from 'lodash';
import { GraphNodeTypes } from '../services/ducks/designerDuck/designerDuck';
import { createTransitMap } from '../services/ducks/workflowDuck/workflowDuck';
import { assertTransitMapsAreEqual } from './saveValidation';

export function uuid6() {
  return `0${Math.floor(Math.random() * 90000) + 10000}`;
}

export function switchToLegacyView() {
  window.location.href = `${window.location.origin}#${window.location.pathname}`;
}

export function switchBackToLegacyViewWithError(err) {
  console.error(err);
  alert(
    `
    A potential problem is found with saving the workflow!
    We advise you to switch back to the old Bridge UI and
    continue editing this workflow there!
    `
  );
  switchToLegacyView();
}

export function parseWorkflow(start, transits) {
  const result = parseWorkflowInternal(start, transits);
  try {
    // An artificial test to see if we can convert the internal representation
    // back to an equivalent transit map.
    const transitsAfterSave = createTransitMap(result.nodes);
    assertTransitMapsAreEqual(transits, transitsAfterSave);
    console.log('Workflow save validation passed!');
  } catch (e) {
    //TODO: Add a properCreate a proper modal window to show up this warning!
    switchBackToLegacyViewWithError(e);
    throw new Error(
      `Workflow save validation failed with error: ${e.message || e}`
    );
  }
  return result;
}

export function parseWorkflowInternal(start, transits) {
  let newStart = '';
  const convertMap = {};

  const parseTransit = (results, node, id) => {
    let lastMod;
    const add = (key, obj) => {
      results[key] = obj;
      if (lastMod && results[lastMod]) {
        results[lastMod].next = [key];
      }
      lastMod = key;
    };

    // Get new UUID
    let uuid = node.testUUID || uuid6(id);
    while (results[uuid] !== undefined) {
      uuid = uuid6();
    }
    convertMap[id] = uuid;
    if (id === start) {
      newStart = uuid;
    }

    if (node.groupName && node.groupName.length) {
      if (node.state.includes('$start')) {
        const { nextState, ...remaining } = node;
        add(uuid, {
          id: uuid,
          type: GraphNodeTypes.GROUP_JUNCTION,
          next: transits[nextState].nextState,
          ...node.runModules[0],
          ...remaining,
        });
      } else if (node.state.includes('$main')) {
        const { nextState, ...remaining } = node;
        add(uuid, {
          id: uuid,
          type: GraphNodeTypes.GROUP_JUNCTION,
          next: nextState,
          isGroupMain: true,
          ...remaining,
        });
      } else {
        const { nextState, state, ...remaining } = node;
        add(uuid, {
          id: uuid,
          type: GraphNodeTypes.GROUP_EXIT,
          next: nextState,
          exit: state.substr(state.indexOf('$') + 1),
          state: state,
          ...remaining,
        });
      }
    } else if (node.nextState && node.nextState.length > 1) {
      delete node.runModules;
      const { nextState, ...remaining } = node;
      add(uuid, {
        id: uuid,
        type: GraphNodeTypes.JUNCTION,
        next: nextState,
        ...remaining,
      });
    } else {
      const { nextState, runModules, ...remaining } = node;
      add(uuid, {
        id: uuid,
        type:
          node.childIterator !== undefined || node.childConversation.length > 0
            ? GraphNodeTypes.CHILD
            : GraphNodeTypes.STATE,
        next: nextState,
        runModules,
        ...remaining,
      });
    }
  };

  const mapIdReferencesToUUIDs = node => ({
    ...node,
    next: _.map(node.next, nextId => {
      return convertMap[nextId];
    }),
  });

  const extractModules = (results, node, id, original) => {
    const add = (key, obj) => {
      results[key] = obj;
    };

    switch (node.type) {
      case GraphNodeTypes.STATE: {
        const {
          next,
          type,
          state,
          question,
          answer,
          retryHelpTxt,
          checkValue,
          ctxToParse,
          groupName,
          ...remaining
        } = node;
        const modules = node.runModules;
        if (modules.length) {
          const childStateID = node.next[0];
          let previousModuleID;
          _.eachRight(modules, (module, index) => {
            // Get new UUID
            let uuid = module.testUUID || uuid6();

            while (
              results[uuid] !== undefined ||
              original[uuid] !== undefined
            ) {
              uuid = uuid6();
            }

            const { moduleID, pluginUUID, moduleData } = module;
            const next =
              index === modules.length - 1 ? childStateID : previousModuleID;
            add(uuid, {
              id: uuid,
              type: GraphNodeTypes.MODULE,
              moduleID,
              pluginUUID,
              moduleData,
              next: next ? [next] : [],
              groupName,
            });
            previousModuleID = uuid;
          });
          results[id] = {
            id,
            type,
            state,
            question,
            answer,
            retryHelpTxt,
            checkValue,
            ctxToParse,
            nextState: next,
            next: [previousModuleID],
            groupName,
            ...remaining,
          };
        } else {
          results[id] = {
            id,
            type,
            state,
            question,
            answer,
            retryHelpTxt,
            checkValue,
            ctxToParse,
            next,
            groupName,
            ...remaining,
          };
        }
        break;
      }
      default:
        results[id] = node;
        break;
    }
  };

  const flagJunctionStates = (results, node, id, original) => {
    if (node.type === GraphNodeTypes.JUNCTION) {
      const nextStates = node.next;
      nextStates.forEach(nodeID => {
        const stateNode = original[nodeID];
        stateNode.isJunctionState = true;
        results[nodeID] = stateNode;
      });
    }
    results[id] = node;
  };

  const flagGroupStates = (results, node, id, original) => {
    if (node.type === GraphNodeTypes.GROUP_EXIT) {
      const nextNodes = node.next;
      nextNodes.forEach(nodeID => {
        const stateNode = original[nodeID];
        if (stateNode) {
          stateNode.isGroupState = true;
          stateNode.groupExitID = id;
          results[nodeID] = stateNode;
        }
      });
    }
    results[id] = node;
  };

  let newNodes = _.chain(transits)
    .transform(parseTransit)
    .mapValues(mapIdReferencesToUUIDs)
    .transform(extractModules)
    .transform(flagJunctionStates)
    .transform(flagGroupStates)
    .value();

  return {
    nodes: newNodes,
    start: newStart,
    convertMap,
  };
}
